/**
 * Bloq Email Admin Hub.
 * Tabs: Provider · Branding · Templates · Triggers · Logs · Preferences · Audience Sync.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Send, Mail, Settings, Layers, FileText, Activity, Sliders, Globe,
  AlertCircle, CheckCircle2, Loader2, Search, Filter, ExternalLink, X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function EmailAdmin() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  // Platform-admin gate. Provider config, From-email, and Support-email
  // are wired to SMTP/sender infrastructure customers can't currently
  // self-serve, so we hide those surfaces from regular users until
  // custom-SMTP is launched. is_admin is set on the seeded founder
  // account and any other accounts an admin promotes server-side.
  const isPlatformAdmin = !!user?.is_admin;
  // Default tab: admins land on Provider, everyone else lands on
  // Branding (which is the first tab visible to them).
  const defaultTab = isPlatformAdmin ? "provider" : "branding";
  return (
    <div className="flex flex-col gap-6" data-testid="email-admin">
      <header>
        <p className="bloq-eyebrow">Settings · Email</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Email notifications.</h1>
        <p className="text-sm text-[#4a4a52] mt-2 max-w-2xl">
          Branded transactional emails, scheduled reminders, customizable templates, and clear logs.
          When no provider is configured, emails are queued and logged — never fake-sent.
        </p>
      </header>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="flex-wrap h-auto">
          {isPlatformAdmin && (
            <TabsTrigger value="provider" data-testid="email-tab-provider"><Send className="w-3.5 h-3.5 mr-1.5" /> Provider</TabsTrigger>
          )}
          <TabsTrigger value="branding" data-testid="email-tab-branding"><Settings className="w-3.5 h-3.5 mr-1.5" /> Branding</TabsTrigger>
          <TabsTrigger value="templates" data-testid="email-tab-templates"><FileText className="w-3.5 h-3.5 mr-1.5" /> Templates</TabsTrigger>
          <TabsTrigger value="triggers" data-testid="email-tab-triggers"><Layers className="w-3.5 h-3.5 mr-1.5" /> Triggers</TabsTrigger>
          <TabsTrigger value="logs" data-testid="email-tab-logs"><Activity className="w-3.5 h-3.5 mr-1.5" /> Logs</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="email-tab-preferences"><Sliders className="w-3.5 h-3.5 mr-1.5" /> Preferences</TabsTrigger>
          <TabsTrigger value="audience" data-testid="email-tab-audience"><Globe className="w-3.5 h-3.5 mr-1.5" /> Audience Sync</TabsTrigger>
        </TabsList>

        {isPlatformAdmin && (
          <TabsContent value="provider" className="mt-5"><ProviderPanel workspace={currentWorkspace} /></TabsContent>
        )}
        <TabsContent value="branding" className="mt-5"><BrandingPanel workspace={currentWorkspace} isAdmin={isPlatformAdmin} /></TabsContent>
        <TabsContent value="templates" className="mt-5"><TemplatesPanel workspace={currentWorkspace} user={user} /></TabsContent>
        <TabsContent value="triggers" className="mt-5"><TriggersPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="logs" className="mt-5"><LogsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="preferences" className="mt-5"><PreferencesPanel workspace={currentWorkspace} user={user} /></TabsContent>
        <TabsContent value="audience" className="mt-5"><AudiencePanel workspace={currentWorkspace} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ===== Provider ===== */
function ProviderPanel({ workspace }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workspace) return;
    api.get("/email/provider/status", { params: { workspace_id: workspace.workspace_id } }).then(r => setData(r.data));
  }, [workspace]);
  if (!data) return <p className="text-sm text-[#82828c]">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="bloq-card p-5 lg:col-span-2 flex flex-col gap-3" data-testid="provider-status">
        <div className="flex items-center justify-between">
          <p className="bloq-headline text-xl text-[#005e7a]">Sending provider</p>
          <span className="bloq-pill" style={{
            background: data.configured ? "#005e7a" : "#b45309",
            color: "#fff",
          }}>{data.configured ? "Connected" : "Setup required"}</span>
        </div>
        <KV label="Active provider" v={data.provider.toUpperCase()} />
        <KV label="From name" v={data.from_name} />
        <KV label="From email" v={data.from_email} />
        <KV label="Reply-to" v={data.reply_to_email || "—"} />
        <KV label="Support email" v={data.support_email} />
        {data.last_sent_at && <KV label="Last successful send" v={new Date(data.last_sent_at).toLocaleString()} />}
        {/* The previous "Last failure:" Postmark error blob was removed
            because (a) it leaked the suppression-list mechanics to the
            UI and (b) was confusing — failures are already visible in
            the Logs tab with full context. */}
      </div>
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Available providers</p>
        <p className="text-xs text-[#82828c] mb-3">Set the env vars below + restart the backend. Keys are NEVER shown in the UI.</p>
        <ul className="flex flex-col gap-2 text-xs">
          {data.available_providers.map(p => (
            <li key={p.name} className="bg-[#f5efe6] p-2 rounded">
              <p className="font-medium text-sm">{p.name}</p>
              <code className="text-[10px] text-[#82828c]">{p.env.join(" + ")}</code>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function KV({ label, v }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-[rgba(26,26,31,0.05)] last:border-0">
      <span className="text-xs uppercase tracking-widest text-[#82828c]">{label}</span>
      <span className="text-sm font-medium">{v}</span>
    </div>
  );
}

/* ===== Branding ===== */
function BrandingPanel({ workspace, isAdmin }) {
  const [form, setForm] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const load = async () => {
    if (!workspace) return;
    const r = await api.get("/email/branding", { params: { workspace_id: workspace.workspace_id } });
    setForm(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  if (!form) return <p className="text-sm text-[#82828c]">Loading…</p>;
  const save = async () => {
    try {
      // Strip platform-admin-only fields when the caller can't see them
      // in the form. The backend silently drops them too, but stripping
      // here keeps the network payload clean.
      const payload = { workspace_id: workspace.workspace_id, ...form };
      if (!isAdmin) {
        delete payload.from_email;
        delete payload.support_email;
        delete payload.support;
      }
      await api.patch("/email/branding", payload);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // Upload a workspace logo. The backend stores it via Emergent Object
  // Storage and returns a public URL we wire straight into the form so
  // the preview + every outgoing email picks it up on save.
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be smaller than 2 MB");
      return;
    }
    const fd = new FormData();
    fd.append("workspace_id", workspace.workspace_id);
    fd.append("file", file);
    setUploadingLogo(true);
    try {
      const r = await api.post("/email/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: r.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove the workspace logo? Emails will fall back to the default.")) return;
    try {
      await api.patch("/email/branding", { workspace_id: workspace.workspace_id, logo_url: "" });
      setForm((f) => ({ ...f, logo_url: "" }));
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bloq-card p-5 flex flex-col gap-3" data-testid="branding-form">
        <p className="bloq-headline text-lg text-[#005e7a]">Brand</p>
        <Field label="From name">
          <input
            className="bloq-input"
            value={form.from_name || ""}
            onChange={e => setForm({ ...form, from_name: e.target.value })}
            placeholder="Acme Inc"
            data-testid="branding-from-name"
          />
        </Field>
        {/* From email + Support email are admin-only for now — they're
            wired to the platform's sender infrastructure and changing
            them on a free/non-SMTP account would break deliverability.
            We'll unhide both once customer-managed SMTP is launched. */}
        {isAdmin && (
          <Field label="From email">
            <input
              className="bloq-input"
              type="email"
              value={form.from_email || ""}
              onChange={e => setForm({ ...form, from_email: e.target.value })}
              placeholder="hello@yourdomain.com"
              data-testid="branding-from-email"
            />
            <p className="mt-1 text-[11px] text-[#82828c]">
              Must be verified at your email provider (Postmark/SendGrid) or sends will be rejected.
            </p>
          </Field>
        )}
        <Field label="Reply-to email">
          <input
            className="bloq-input"
            value={form.reply_to || ""}
            onChange={e => setForm({ ...form, reply_to: e.target.value })}
            data-testid="branding-reply-to"
          />
        </Field>
        {isAdmin && (
          <Field label="Support email">
            <input
              className="bloq-input"
              value={form.support_email || ""}
              onChange={e => setForm({ ...form, support_email: e.target.value })}
              data-testid="branding-support-email"
            />
          </Field>
        )}

        {/* Logo: file upload (replaces the old URL input). The logo is
            applied to every email template AND the in-app sidebar logo. */}
        <Field label="Logo">
          <div className="flex items-center gap-3">
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt="Workspace logo"
                className="h-12 max-w-[160px] object-contain bg-[#f5efe6] px-2 py-1"
                data-testid="branding-logo-preview"
              />
            ) : (
              <div className="h-12 px-3 flex items-center text-[11px] text-[#82828c] bg-[#f5efe6]">No logo uploaded</div>
            )}
            <label className="bloq-btn-secondary cursor-pointer text-xs inline-flex items-center" data-testid="branding-logo-upload-label">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                className="hidden"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
                data-testid="branding-logo-upload-input"
              />
              {uploadingLogo ? "Uploading…" : (form.logo_url ? "Replace" : "Upload logo")}
            </label>
            {form.logo_url && (
              <button
                type="button"
                onClick={removeLogo}
                className="text-xs text-[#c43838] hover:underline"
                data-testid="branding-logo-remove"
              >
                Remove
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-[#82828c]">PNG, JPG, SVG, WebP, or GIF. Max 2 MB.</p>
        </Field>

        <Field label="Accent color">
          <input
            type="color"
            className="bloq-input h-10"
            value={form.accent_color || "#005e7a"}
            onChange={e => setForm({ ...form, accent_color: e.target.value })}
            data-testid="branding-accent-color"
          />
        </Field>

        {/* Sign-off is OPTIONAL. Leaving it blank means emails render with
            no "— …" sign-off line at all. We don't autofill placeholders. */}
        <Field label="Sign-off (optional)">
          <input
            className="bloq-input"
            value={form.sign_off || ""}
            onChange={e => setForm({ ...form, sign_off: e.target.value })}
            placeholder="Leave blank for no sign-off"
            data-testid="branding-sign-off"
          />
          <p className="mt-1 text-[11px] text-[#82828c]">If left blank, emails will not include a sign-off line.</p>
        </Field>
        <Field label="Footer text"><textarea rows={2} className="bloq-input" value={form.footer_text || ""} onChange={e => setForm({ ...form, footer_text: e.target.value })} /></Field>
        <Field label="Business address"><textarea rows={2} className="bloq-input" value={form.business_address || ""} onChange={e => setForm({ ...form, business_address: e.target.value })} placeholder="123 Main St, Suite 100, …" /></Field>
        <label className="flex items-center justify-between p-3 bg-[#f5efe6] rounded-md">
          <span className="text-sm">Hide "Powered by Bloq"</span>
          <Switch checked={!!form.hide_powered_by} onCheckedChange={v => setForm({ ...form, hide_powered_by: v })} />
        </label>
        <button onClick={save} className="bloq-btn-primary mt-2" data-testid="branding-save">Save branding</button>
      </div>
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Preview</p>
        <BrandPreview form={form} />
      </div>
    </div>
  );
}

function BrandPreview({ form }) {
  // Sign-off rendering: ONLY shown when the workspace explicitly set one.
  // Blank means "don't render the sign-off line" — matches the actual
  // outgoing email behavior so the preview is honest.
  const signOff = (form.sign_off || "").trim();
  return (
    <div className="rounded-lg overflow-hidden bg-[#f5efe6] p-4">
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="text-center pb-3 border-b border-[#f5efe6]">
          {form.logo_url ? (
            <img src={form.logo_url} alt="" className="h-8 mx-auto" />
          ) : (
            <span className="inline-block px-3 py-1 rounded-md text-white text-xs font-semibold" style={{ background: form.accent_color || "#005e7a" }}>{form.from_name || "Bloq"}</span>
          )}
        </div>
        <div className="py-4 text-sm">
          <p className="text-lg font-semibold mb-2">Your booking is confirmed</p>
          <p className="text-[#4a4a52]">Hi Alex — your booking with Sam Lee is locked in. Looking forward to it.</p>
          <button className="mt-4 px-5 py-2.5 rounded-md text-white text-sm font-medium" style={{ background: form.accent_color || "#005e7a" }}>Open meeting room</button>
          {signOff && <p className="mt-4 text-[#4a4a52]">— {signOff}</p>}
        </div>
        <div className="pt-3 border-t border-[#f5efe6] text-xs text-[#82828c]">
          {form.footer_text && <p>{form.footer_text}</p>}
          {form.business_address && <p>{form.business_address}</p>}
          {!form.hide_powered_by && <p className="mt-2 text-[10px]">Powered by Bloq</p>}
        </div>
      </div>
    </div>
  );
}

/* ===== Templates ===== */
function TemplatesPanel({ workspace, user }) {
  const [rows, setRows] = useState([]);
  // `audience` controls whether we ask the backend for customer-facing
  // (user) templates or platform-owned (security/billing/account) ones.
  // Workspace admins are forced to "user". Bloq platform admins (the
  // `is_admin` flag) can switch between the two via a sub-tab.
  const isPlatformAdmin = !!user?.is_admin;
  const [audience, setAudience] = useState("user");
  const [filter, setFilter] = useState({ category: "all", search: "", customized: false });
  const [edit, setEdit] = useState(null);
  const load = async () => {
    if (!workspace) return;
    const params = { workspace_id: workspace.workspace_id };
    if (filter.category !== "all") params.category = filter.category;
    if (filter.search) params.search = filter.search;
    if (filter.customized) params.only_customized = true;
    // Always send the audience query so the FE always controls the
    // grouping (rather than letting the BE default for platform admins).
    params.audience = audience;
    const r = await api.get("/email/templates", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, filter.category, filter.customized, audience]);
  const categories = useMemo(() => Array.from(new Set(rows.map(r => r.category))).sort(), [rows]);
  return (
    <div className="flex flex-col gap-4" data-testid="templates-list">
      {isPlatformAdmin && (
        <div
          className="inline-flex rounded-md bg-[#f5efe6] p-1 w-fit"
          data-testid="templates-audience-switcher"
        >
          <button
            type="button"
            onClick={() => setAudience("user")}
            className={`text-xs px-3 py-1.5 rounded transition-colors ${
              audience === "user" ? "bg-white shadow-sm font-medium" : "text-[#4a4a52] hover:text-[#1a1a1f]"
            }`}
            data-testid="templates-audience-user"
          >
            Customer emails
          </button>
          <button
            type="button"
            onClick={() => setAudience("platform")}
            className={`text-xs px-3 py-1.5 rounded transition-colors ${
              audience === "platform" ? "bg-white shadow-sm font-medium" : "text-[#4a4a52] hover:text-[#1a1a1f]"
            }`}
            data-testid="templates-audience-platform"
          >
            Platform emails
          </button>
        </div>
      )}
      {audience === "platform" && (
        <div className="bloq-card p-3 border-l-4 border-l-[#b45309] bg-[rgba(180,83,9,0.04)] text-xs text-[#7c3a06]">
          <strong>Platform emails.</strong> These cover account, security, and billing flows
          (password reset, email verification, payment failure, workspace invites). They&apos;re
          signed by the Bloq platform domain and only editable by Bloq admins — workspace
          owners don&apos;t see them.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#82828c]" />
          <input
            className="bloq-input pl-9" placeholder="Search by name, subject, key…"
            value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })}
            onKeyDown={e => e.key === "Enter" && load()} data-testid="templates-search"
          />
        </div>
        <Select value={filter.category} onValueChange={v => setFilter({ ...filter, category: v })}>
          <SelectTrigger className="w-48" data-testid="templates-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={filter.customized} onCheckedChange={v => setFilter({ ...filter, customized: v })} />
          Customized only
        </label>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#82828c] text-center py-10">
          No {audience === "platform" ? "platform" : "customer"} email templates match.
        </p>
      ) : (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map(t => (
          <div key={t.template_key} className="bloq-card p-5 flex flex-col gap-2" data-testid={`tpl-${t.template_key}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="bloq-headline text-base">{t.name}</p>
              <div className="flex flex-col gap-1 items-end shrink-0">
                {t.audience === "platform" && (
                  <span
                    className="bloq-pill text-[10px] font-semibold"
                    style={{ background: "#b45309", color: "#fff" }}
                    title="Platform-managed — only editable by Bloq admins"
                  >
                    Platform
                  </span>
                )}
                {t.customized && <span className="bloq-pill text-[10px]" style={{ background: "#005e7a", color: "#fff" }}>Customized</span>}
              </div>
            </div>
            <p className="text-xs text-[#82828c]">{t.category}</p>
            <p className="text-sm text-[#4a4a52] line-clamp-2"><strong>Subject:</strong> {t.subject}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              <button onClick={() => setEdit(t)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`edit-tpl-${t.template_key}`}>Edit</button>
              <button onClick={async () => { await api.post(`/email/templates/${t.template_key}/test-send`, { workspace_id: workspace.workspace_id }); toast.success("Test queued — check Logs"); }} className="bloq-btn-secondary text-xs px-3 py-1.5">Test send</button>
            </div>
          </div>
        ))}
      </div>
      )}
      {edit && <TemplateEditor template={edit} workspace={workspace} onClose={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function TemplateEditor({ template, workspace, onClose }) {
  const [tpl, setTpl] = useState({ ...template });
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState("html");
  const refreshPreview = async () => {
    try {
      const r = await api.post(`/email/templates/${template.template_key}/preview`, {
        workspace_id: workspace.workspace_id,
        // Pass the CURRENT (potentially unsaved) draft so the preview
        // reflects what the user is typing, not the last persisted copy.
        subject: tpl.subject,
        preheader: tpl.preheader,
        html_body: tpl.html_body,
        text_body: tpl.text_body,
      });
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refreshPreview(); /* eslint-disable-next-line */ }, []);
  const save = async () => {
    try {
      await api.patch(`/email/templates/${template.template_key}`, {
        workspace_id: workspace.workspace_id,
        subject: tpl.subject, preheader: tpl.preheader, html_body: tpl.html_body,
        text_body: tpl.text_body, enabled: tpl.enabled,
      });
      toast.success("Template saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Reset to system default?")) return;
    try { await api.post(`/email/templates/${template.template_key}/reset`, null, { params: { workspace_id: workspace.workspace_id } });
      toast.success("Reset to default"); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async () => {
    try { await api.post(`/email/templates/${template.template_key}/test-send`, { workspace_id: workspace.workspace_id });
      toast.success("Test queued. Check Logs."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
        </DialogHeader>
        <div className="grid lg:grid-cols-2 gap-4 mt-2">
          <div className="flex flex-col gap-3">
            <Field label="Subject"><input className="bloq-input" value={tpl.subject} onChange={e => setTpl({ ...tpl, subject: e.target.value })} data-testid="tpl-subject" /></Field>
            <Field label="Preheader"><input className="bloq-input" value={tpl.preheader || ""} onChange={e => setTpl({ ...tpl, preheader: e.target.value })} /></Field>
            <Field label="HTML body">
              <textarea rows={14} className="bloq-input font-mono text-xs" value={tpl.html_body} onChange={e => setTpl({ ...tpl, html_body: e.target.value })} data-testid="tpl-html" />
            </Field>
            <Field label="Plain text body">
              <textarea rows={6} className="bloq-input font-mono text-xs" value={tpl.text_body || ""} onChange={e => setTpl({ ...tpl, text_body: e.target.value })} />
            </Field>
            <label className="flex items-center justify-between p-3 bg-[#f5efe6] rounded-md">
              <span className="text-sm">Template enabled</span>
              <Switch checked={!!tpl.enabled} onCheckedChange={v => setTpl({ ...tpl, enabled: v })} />
            </label>
            <div className="text-xs text-[#82828c] bg-[#f5efe6] p-3 rounded-md">
              <p className="font-medium mb-1">Available variables:</p>
              <div className="flex flex-wrap gap-1">
                {(preview?.all_variables || []).map(v => <code key={v} className="bg-white px-1.5 py-0.5 rounded text-[10px]">{`{{${v}}}`}</code>)}
              </div>
              {preview?.missing_variables?.length > 0 && (
                <p className="mt-2 text-[#b45309]">⚠ Missing values for: {preview.missing_variables.join(", ")}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2 text-xs">
              {["html", "text"].map(t => (
                <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded ${tab === t ? "bg-[#005e7a] text-white" : "bg-[#f5efe6]"}`}>
                  {t === "html" ? "Desktop preview" : "Plain text"}
                </button>
              ))}
              <button onClick={refreshPreview} className="px-3 py-1.5 rounded bg-[#f5efe6]">Refresh preview</button>
            </div>
            {tab === "html" && (
              <iframe
                title="email-preview" srcDoc={preview?.html || ""}
                className="bloq-card w-full h-[500px] bg-white"
                sandbox="allow-same-origin"
                data-testid="tpl-preview-iframe"
              />
            )}
            {tab === "text" && (
              <pre className="bloq-card p-4 text-xs whitespace-pre-wrap font-mono">{preview?.text || ""}</pre>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4 flex flex-wrap gap-2">
          <button onClick={reset} className="bloq-btn-secondary text-xs px-3 py-1.5">Reset to default</button>
          <button onClick={test} className="bloq-btn-secondary text-xs px-3 py-1.5">Send test</button>
          <button onClick={save} className="bloq-btn-primary" data-testid="tpl-save">Save changes</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===== Triggers ===== */
function TriggersPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    if (!workspace) return;
    setRows((await api.get("/email/triggers", { params: { workspace_id: workspace.workspace_id } })).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const toggle = async (t, enabled) => {
    if (t.category === "Security" && !enabled) { toast.error("Security triggers cannot be disabled"); return; }
    try {
      await api.patch(`/email/triggers/${t.trigger_key}`, { workspace_id: workspace.workspace_id, enabled });
      toast.success(`${t.name} ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const byCategory = rows.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});
  return (
    <div className="flex flex-col gap-5" data-testid="triggers-list">
      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="bloq-card p-5">
          <p className="bloq-headline text-lg mb-3 text-[#005e7a]">{cat}</p>
          <div className="flex flex-col gap-2">
            {items.map(t => (
              <div key={t.trigger_key} className="flex items-center justify-between bg-[#f5efe6] p-3 rounded-md" data-testid={`trigger-${t.trigger_key}`}>
                <div>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-[10px] text-[#82828c]"><code>{t.trigger_key}</code> → <code>{t.template_key}</code></p>
                </div>
                <Switch
                  checked={!!t.enabled}
                  onCheckedChange={v => toggle(t, v)}
                  disabled={t.category === "Security"}
                  data-testid={`toggle-${t.trigger_key}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===== Logs ===== */
function LogsPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ status: "all", search: "", template_key: "" });
  const [selected, setSelected] = useState(null);
  const load = async () => {
    if (!workspace) return;
    const params = { workspace_id: workspace.workspace_id };
    if (filter.status !== "all") params.status = filter.status;
    if (filter.search) params.search = filter.search;
    if (filter.template_key) params.template_key = filter.template_key;
    setRows((await api.get("/email/logs", { params })).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, filter.status]);
  return (
    <div className="flex flex-col gap-4" data-testid="logs-list">
      <div className="flex flex-wrap gap-3">
        <input placeholder="Search recipient or subject…" className="bloq-input flex-1 min-w-[200px]"
          value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })}
          onKeyDown={e => e.key === "Enter" && load()} data-testid="logs-search" />
        <Select value={filter.status} onValueChange={v => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="provider_not_configured">Provider not configured</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#82828c] bloq-card p-5">No email events yet.</p>
      ) : (
        <div className="bloq-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
                <th className="px-3 py-3">When</th>
                <th className="px-3 py-3">Template</th>
                <th className="px-3 py-3">Recipient</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Subject</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.log_id} className="hover:bg-[#f5efe6]" data-testid={`log-${l.log_id}`}>
                  <td className="px-3 py-3 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-3 py-3 font-mono text-xs">{l.template_key}</td>
                  <td className="px-3 py-3 text-xs">{l.recipient_email}</td>
                  <td className="px-3 py-3"><StatusPill status={l.status} /></td>
                  <td className="px-3 py-3 text-xs truncate max-w-[280px]">{l.subject || "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => setSelected(l)} className="bloq-btn-secondary text-xs px-3 py-1.5">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && <LogDetail log={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    sent: { bg: "#005e7a", fg: "#fff" },
    pending: { bg: "#b45309", fg: "#fff" },
    failed: { bg: "#c43838", fg: "#fff" },
    skipped: { bg: "#4a4a52", fg: "#fff" },
    canceled: { bg: "#4a4a52", fg: "#fff" },
    provider_not_configured: { bg: "#b45309", fg: "#fff" },
    queued: { bg: "#1e3a8a", fg: "#fff" },
  };
  const c = colors[status] || { bg: "#f5efe6", fg: "#1a1a1f" };
  return <span className="bloq-pill text-[10px]" style={{ background: c.bg, color: c.fg }}>{status}</span>;
}

function LogDetail({ log, onClose }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    api.get(`/email/logs/${log.log_id}`).then(r => setFull(r.data));
  }, [log]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="log-detail">
        <DialogHeader><DialogTitle>{log.template_key}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-2 mt-2 text-sm">
          <KV label="Recipient" v={log.recipient_email} />
          <KV label="Subject" v={log.subject || "—"} />
          <KV label="Status" v={log.status} />
          <KV label="Provider" v={log.provider || "—"} />
          {log.error_message && (
            <div className="bg-[#fef2f2] p-3 rounded-md text-[#c43838] text-xs"><strong>Error:</strong> {log.error_message}</div>
          )}
          {full?.html_snapshot && (
            <iframe title="snapshot" srcDoc={full.html_snapshot} className="bloq-card w-full h-[500px] bg-white mt-3" sandbox="allow-same-origin" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===== Preferences ===== */
function PreferencesPanel({ workspace, user }) {
  const [data, setData] = useState({ workspace: [], user: [] });
  const [triggers, setTriggers] = useState([]);
  const load = async () => {
    if (!workspace) return;
    const [p, t] = await Promise.all([
      api.get("/email/preferences", { params: { workspace_id: workspace.workspace_id } }),
      api.get("/email/triggers", { params: { workspace_id: workspace.workspace_id } }),
    ]);
    setData(p.data); setTriggers(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const findUserPref = (key) => data.user.find(p => p.trigger_key === key);
  const set = async (trigger_key, email_enabled) => {
    try {
      await api.patch("/email/preferences", {
        workspace_id: workspace.workspace_id, scope: "user", trigger_key, email_enabled,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="bloq-card p-5" data-testid="preferences">
      <p className="bloq-headline text-lg mb-1 text-[#005e7a]">My notification preferences</p>
      <p className="text-xs text-[#82828c] mb-4">Security emails (verification, password reset) always send.</p>
      <div className="flex flex-col gap-2">
        {triggers.filter(t => t.category !== "Security").map(t => {
          const pref = findUserPref(t.trigger_key);
          const enabled = pref ? pref.email_enabled : true;
          return (
            <div key={t.trigger_key} className="flex items-center justify-between bg-[#f5efe6] p-3 rounded-md">
              <div>
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-[10px] text-[#82828c]">{t.category}</p>
              </div>
              <Switch checked={enabled} onCheckedChange={v => set(t.trigger_key, v)} data-testid={`pref-${t.trigger_key}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== Audience Sync ===== */
function AudiencePanel({ workspace }) {
  const [status, setStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!workspace) return;
    api.get("/email/audience/status", { params: { workspace_id: workspace.workspace_id } }).then(r => setStatus(r.data));
    api.get("/contacts", { params: { workspace_id: workspace.workspace_id } }).then(r => setContacts(r.data));
  }, [workspace]);
  if (!status) return <p className="text-sm text-[#82828c]">Loading…</p>;
  const sync = async (provider) => {
    if (!selected.length) { toast.error("Select contacts first"); return; }
    setBusy(provider);
    try {
      const r = await api.post("/email/audience/sync", { workspace_id: workspace.workspace_id, provider, contact_ids: selected });
      if (r.data.configuration_required) {
        toast.error(`${provider} not configured — admin must set API keys in env`);
      } else {
        toast.success(`${provider}: synced ${r.data.synced}, skipped ${r.data.skipped}, errors ${r.data.errors?.length || 0}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Sync contacts to your marketing platform</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {["mailerlite", "constant_contact", "mailchimp"].map(p => {
            const meta = status[p];
            return (
              <div key={p} className="bg-[#f5efe6] p-4 rounded-md flex flex-col gap-2" data-testid={`audience-${p}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium capitalize">{p.replace("_", " ")}</p>
                  <span className="bloq-pill text-[10px]" style={{ background: meta.configured ? "#005e7a" : "#b45309", color: "#fff" }}>
                    {meta.configured ? "Connected" : "Setup required"}
                  </span>
                </div>
                <p className="text-xs text-[#82828c]">{meta.purpose}</p>
                <button
                  onClick={() => sync(p)}
                  disabled={!meta.configured || busy === p || !selected.length}
                  className={`text-xs px-3 py-1.5 rounded-md mt-1 ${meta.configured && selected.length ? "bloq-btn-primary" : "bg-[#e5e5e5] text-[#82828c] cursor-not-allowed"}`}
                  data-testid={`sync-${p}`}
                >
                  {busy === p ? "Syncing…" : `Sync ${selected.length || 0} contacts`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bloq-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="bloq-headline text-lg text-[#005e7a]">Contacts to sync</p>
          <button
            onClick={() => setSelected(selected.length === contacts.length ? [] : contacts.map(c => c.contact_id))}
            className="bloq-btn-secondary text-xs px-3 py-1.5"
          >
            {selected.length === contacts.length ? "Deselect all" : "Select all"}
          </button>
        </div>
        <div className="flex flex-col gap-1 max-h-96 overflow-y-auto">
          {contacts.map(c => (
            <label key={c.contact_id} className="flex items-center gap-3 p-2 rounded hover:bg-[#f5efe6] cursor-pointer">
              <input
                type="checkbox" checked={selected.includes(c.contact_id)}
                onChange={() => setSelected(s => s.includes(c.contact_id) ? s.filter(x => x !== c.contact_id) : [...s, c.contact_id])}
                data-testid={`contact-check-${c.contact_id}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.name}</p>
                <p className="text-[10px] text-[#82828c] truncate">{c.email}</p>
              </div>
            </label>
          ))}
          {contacts.length === 0 && <p className="text-sm text-[#82828c]">No contacts yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ===== Reusable ===== */
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
